// A goods-handover record for a bulk/reseller order — same branded header/
// footer/watermark treatment as InvoiceDocument.tsx (this file is meant to
// be read alongside that one; anything not documented here follows the
// same reasoning as there, especially the print-safety comments on
// minHeight/grid/break-inside). Computed entirely at render time from an
// existing invoice — no dedicated DB row, same technique as the Mushak
// Chalan No. (doc_number with INV- swapped for CHA-).
import type { ReactNode } from 'react';
import { Landmark, Mail, Globe, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { quantityToWords } from '@/lib/numberToWords';
import { challanText } from '@/lib/challanTextFields';
import { RefundWatermark, type InvoiceRefund } from './RefundWatermark';

export interface DeliveryChallanTemplateData {
  issuer_name: string | null;
  concern_line: string | null;
  issuer_business_number: string | null;
  trade_license_no: string | null;
  issuer_address: string | null;
  issuer_contact: string | null;
  issuer_email: string | null;
  issuer_website: string | null;
  logo_url: string | null;
  watermark_url: string | null;
  social_url: string | null;
  // "Handed Over By" — set once in Invoice Identity settings, printed on
  // every challan. Falls back to just issuer_name (no designation line) if
  // left blank, same pattern as the invoice's own optional fields.
  challan_authorized_person_name: string | null;
  challan_authorized_person_designation: string | null;
  text_overrides?: Record<string, string> | null;
}

export interface DeliveryChallanItem {
  description: string;
  unit: string;
  quantity: number;
  // Typed in just before printing (see the view page), not stored anywhere
  // — applied identically to every row, matching the common case of one
  // packing note per shipment rather than per line.
  remarks: string;
}

export interface DeliveryChallanData {
  challan_number: string | null;
  issue_date: string | null;
  company_name: string | null;
  // Falls back to just company_name (Received By still prints, just
  // without a designation line) when the order has no linked partner —
  // this document is available for any order, not only reseller ones.
  contact_person: string | null;
  contact_designation: string | null;
  mobile: string | null;
  address: string | null;
  items: DeliveryChallanItem[];
}

const NAVY = '#1B2A4A';
const NAVY_DEEP = '#0E1A30';
const GOLD = '#AE8A4C';
const INK = '#26324A';
const MUTED = '#6B7280';
const FAINT = '#9CA3AF';
const HAIRLINE = '#E7E9EE';
const SOFT_BG = '#F6F7FB';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Same label/value pairing InvoiceDocument.tsx uses for From/Bill To — reused
// here so the customer-details block reads as part of the same document
// system instead of a plain HTML table.
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[8.5px] font-bold uppercase tracking-[0.14em] mb-[3px]" style={{ color: FAINT }}>
        {label}
      </p>
      <div className="text-[12px] leading-[1.45]" style={{ color: INK }}>
        {children}
      </div>
    </div>
  );
}

export function DeliveryChallanDocument({
  template,
  request,
  copyLabel,
  refund,
  cancelled,
  cancelReason,
}: {
  template: DeliveryChallanTemplateData;
  request: DeliveryChallanData;
  // Printed as a small tag above the header — two copies (one for the
  // customer to keep, one for internal office records) are the norm for a
  // physical handover document; omit for a single-copy render (e.g. the
  // settings live preview).
  copyLabel?: 'customer' | 'office';
  refund?: InvoiceRefund | null;
  /** core_orders.status === 'cancelled' — stamps "CANCELLED" instead. */
  cancelled?: boolean;
  cancelReason?: string | null;
}) {
  const t = (key: string, vars?: Record<string, string | number>) => challanText(template.text_overrides, key, vars);
  const brandName = template.issuer_name?.trim() || 'The.K Seoul';
  const concernLine = template.concern_line?.trim();
  const concernSentence = concernLine ? `${brandName} is ${concernLine}` : null;
  const legalBits = [
    template.issuer_business_number ? `BIN: ${template.issuer_business_number}` : null,
    template.trade_license_no ? `Trade License No: ${template.trade_license_no}` : null,
  ].filter(Boolean);

  const website = template.issuer_website?.trim();
  const socialUrl = template.social_url?.trim();
  const qrValue = socialUrl
    ? /^https?:\/\//i.test(socialUrl)
      ? socialUrl
      : `https://${socialUrl}`
    : website
      ? /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`
      : template.issuer_email
        ? `mailto:${template.issuer_email.trim()}`
        : null;

  const totalQuantity = request.items.reduce((sum, item) => sum + item.quantity, 0);
  const unitWord = totalQuantity === 1 ? t('unit_piece') : t('unit_pieces');
  const quantityWords = quantityToWords(totalQuantity);

  return (
    <div
      className="challan-doc bg-white mx-auto relative"
      style={{
        width: '210mm',
        minHeight: '220mm',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        fontFamily: "'Inter', system-ui, sans-serif",
        boxSizing: 'border-box',
        color: INK,
      }}
    >
      {/* Watermark — identical treatment to InvoiceDocument.tsx. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none flex items-center justify-center challan-doc-watermark-wrapper" aria-hidden="true">
        {template.watermark_url ? (
          <img
            src={template.watermark_url}
            alt=""
            crossOrigin="anonymous"
            className="challan-doc-watermark"
            style={{ width: '380px', maxHeight: '380px', objectFit: 'contain', opacity: 0.08, filter: 'grayscale(1)' }}
          />
        ) : (
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: '480px', lineHeight: 1, color: NAVY, opacity: 0.035 }}>K</div>
        )}
      </div>
      <RefundWatermark refund={refund} cancelled={cancelled} cancelReason={cancelReason} />

      {/* Grid row 1 (auto height) — same top-anchored flow as InvoiceDocument. */}
      <div className="relative" style={{ padding: '8mm 16mm 0', boxSizing: 'border-box' }}>
        {copyLabel && (
          <p className="m-0 mb-1.5 text-right text-[9px] font-bold uppercase" style={{ letterSpacing: '0.16em', color: FAINT }}>
            {copyLabel === 'customer' ? t('copy_label_customer') : t('copy_label_office')}
          </p>
        )}
        <div className="flex items-center justify-between gap-8">
          <div className="flex items-center gap-3.5 min-w-0">
            {template.logo_url ? (
              <img src={template.logo_url} alt={brandName} crossOrigin="anonymous" className="flex-shrink-0 object-contain" style={{ height: '76px', maxWidth: '260px' }} />
            ) : (
              <h1 className="m-0" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: '30px', letterSpacing: '-0.015em', lineHeight: 1.05, color: NAVY }}>
                {brandName}
              </h1>
            )}
          </div>
          <div className="text-right flex-shrink-0 doc-badge" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`, borderRadius: '8px', padding: '8px 14px', minWidth: '140px' }}>
            <p className="m-0 text-[9.5px] font-bold uppercase" style={{ letterSpacing: '0.18em', color: GOLD }}>
              {t('doc_label')}
            </p>
            <p
              className="mt-1 mb-0 text-white doc-badge-value"
              style={{ fontFamily: "'Inter', system-ui, sans-serif", fontWeight: 700, fontSize: '14px', letterSpacing: '0.01em', fontVariantNumeric: 'tabular-nums' }}
            >
              {request.challan_number || 'DRAFT'}
            </p>
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${GOLD}`, opacity: 1 }} />
        <div style={{ borderTop: `2px solid ${NAVY}`, marginTop: '3px' }} />

        {/* Customer details — one "Deliver To" address block (name bold,
            supporting lines muted below it) exactly like InvoiceDocument's
            own Bill To field, instead of a grid of individually-labeled
            values — a repeated small-caps label on every single field read
            as dense/form-like; a single label with a natural address-block
            underneath reads as considered, and matches the rest of the
            document system exactly. */}
        <div className="grid mt-4" style={{ gridTemplateColumns: '1.15fr 0.75fr', gap: '22px' }}>
          <Field label={t('label_deliver_to')}>
            <p className="font-semibold m-0">{request.company_name || '—'}</p>
            {request.contact_person && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {request.contact_person}
              </p>
            )}
            {request.mobile && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {request.mobile}
              </p>
            )}
            {request.address && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {request.address}
              </p>
            )}
          </Field>
          <Field label={t('label_date')}>{formatDate(request.issue_date)}</Field>
        </div>

        {/* Items */}
        <table className="w-full mt-6" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: SOFT_BG }}>
              <th className="text-center py-2.5 pl-3 text-[9px] font-bold uppercase rounded-l-md" style={{ width: '36px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_sl')}
              </th>
              <th className="text-left py-2.5 text-[9px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: MUTED }}>
                {t('th_description')}
              </th>
              <th className="text-center py-2.5 text-[9px] font-bold uppercase" style={{ width: '60px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_unit')}
              </th>
              <th className="text-center py-2.5 text-[9px] font-bold uppercase" style={{ width: '70px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_quantity')}
              </th>
              <th className="text-left py-2.5 pr-3 text-[9px] font-bold uppercase rounded-r-md" style={{ width: '150px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_remarks')}
              </th>
            </tr>
          </thead>
          <tbody>
            {request.items.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-[11px]" style={{ color: FAINT }}>
                  {t('empty_items')}
                </td>
              </tr>
            ) : (
              request.items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
                  <td className="py-2.5 pl-3 text-[11.5px] text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {idx + 1}
                  </td>
                  <td className="py-2.5 text-[11.5px]">{item.description}</td>
                  <td className="py-2.5 text-[11.5px] text-center">{item.unit}</td>
                  <td className="py-2.5 text-[11.5px] text-center font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: NAVY }}>
                    {item.quantity}
                  </td>
                  <td className="py-2.5 pr-3 text-[11.5px]" style={{ color: MUTED }}>
                    {item.remarks || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="flex justify-end mt-3">
          <p className="m-0 text-[11.5px] font-semibold">{t('label_total_quantity', { quantity: totalQuantity, words: quantityWords, unit: unitWord })}</p>
        </div>

        {/* Declaration */}
        <div className="mt-6 rounded-md p-3" style={{ background: SOFT_BG, borderLeft: `3px solid ${GOLD}` }}>
          <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#8A6A34' }}>
            {t('label_declaration')}
          </p>
          <p className="m-0 mt-1.5 text-[11px] leading-[1.6]" style={{ color: '#374151' }}>
            {t('declaration_text', { quantity: totalQuantity, words: quantityWords, unit: unitWord.toLowerCase() })}
          </p>
        </div>

        {/* Handed Over By / Received By */}
        <div className="grid mt-20" style={{ gridTemplateColumns: '1fr 1fr', gap: '22px' }}>
          <div className="text-center">
            <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', margin: '0 24px' }}>
              <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.14em', color: FAINT }}>
                {t('label_handed_over_by')}
              </p>
              <p className="m-0 mt-2 text-[11.5px] font-semibold" style={{ color: NAVY }}>
                {template.challan_authorized_person_name || brandName}
              </p>
              {template.challan_authorized_person_designation && (
                <p className="m-0 text-[9.5px]" style={{ color: MUTED }}>
                  {template.challan_authorized_person_designation}
                </p>
              )}
              {/* Only add the company-name sub-line when a real person is
                  named above — otherwise that name IS brandName already
                  (the fallback on the line above), and repeating it reads as
                  a rendering glitch rather than a "Name / Company" pair. */}
              {template.challan_authorized_person_name && (
                <p className="m-0 text-[9.5px]" style={{ color: MUTED }}>
                  {brandName}
                </p>
              )}
            </div>
          </div>
          <div className="text-center">
            <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', margin: '0 24px' }}>
              <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.14em', color: FAINT }}>
                {t('label_received_by')}
              </p>
              <p className="m-0 mt-2 text-[11.5px] font-semibold" style={{ color: NAVY }}>
                {request.contact_person || request.company_name || '—'}
              </p>
              {request.contact_designation && (
                <p className="m-0 text-[9.5px]" style={{ color: MUTED }}>
                  {request.contact_designation}
                </p>
              )}
              {request.contact_person && request.company_name && (
                <p className="m-0 text-[9.5px]" style={{ color: MUTED }}>
                  {request.company_name}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grid row 2 ("1fr" above) — same bottom-anchored footer as InvoiceDocument.
          Given a top padding here (unlike InvoiceDocument's footer) because the
          QR row's right side used to be empty space, giving it breathing room
          from the Received By block above for free; now that the thank-you
          message fills that space, the row needs its own explicit gap instead. */}
      <div className="relative flex flex-col justify-end challan-doc-footer" style={{ padding: '10mm 16mm 8mm', boxSizing: 'border-box' }}>
        <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <div className="flex items-center justify-between" style={{ minHeight: '40px' }}>
            {qrValue ? (
              <div className="flex items-center gap-3">
                <div className="rounded-lg" style={{ padding: '7px', border: `1px solid ${GOLD}` }}>
                  <QRCodeSVG value={qrValue} size={44} fgColor={NAVY} bgColor="transparent" level="M" />
                </div>
                <p className="m-0 text-[9px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: '#8A6A34', whiteSpace: 'pre-line' }}>
                  {t('label_scan_to_connect')}
                </p>
              </div>
            ) : (
              <div />
            )}
            <p className="m-0 text-[10.5px] font-bold text-right" style={{ color: NAVY, letterSpacing: '0.01em' }}>
              {t('thank_you_message', { brand: brandName })}
            </p>
          </div>

          <div
            className="mt-5 text-center"
            style={{
              background: `linear-gradient(180deg, ${SOFT_BG} 0%, #F0F2F7 100%)`,
              borderTop: `2px solid ${GOLD}`,
              borderRadius: '12px 12px 0 0',
              padding: '14px 26px 12px',
              marginLeft: '-16mm',
              marginRight: '-16mm',
              marginBottom: '-8mm',
            }}
          >
            {concernSentence && (
              <p className="m-0 text-[10.5px] font-semibold" style={{ color: NAVY, letterSpacing: '0.01em' }}>
                {concernSentence}
              </p>
            )}
            <div className="mx-auto" style={{ width: '30px', height: '1.5px', background: GOLD, marginTop: '8px', marginBottom: '9px' }} />
            <div className="flex items-center justify-center flex-wrap" style={{ columnGap: '18px', rowGap: '5px' }}>
              {legalBits.length > 0 && (
                <span className="inline-flex items-center gap-1" style={{ color: FAINT }}>
                  <Landmark className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px]" style={{ letterSpacing: '0.02em' }}>
                    {legalBits.join('  ·  ')}
                  </span>
                </span>
              )}
              {template.issuer_email && (
                <span className="inline-flex items-center gap-1" style={{ color: '#3D5A8A' }}>
                  <Mail className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px] font-medium">{template.issuer_email}</span>
                </span>
              )}
              {template.issuer_website && (
                <span className="inline-flex items-center gap-1" style={{ color: '#3D5A8A' }}>
                  <Globe className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px] font-medium">{template.issuer_website}</span>
                </span>
              )}
              {template.issuer_address && (
                <span className="inline-flex items-center gap-1" style={{ color: '#B4BAC6' }}>
                  <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px]">{template.issuer_address}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
